import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { MeetingDto, MyProxyGivenToDto } from '../../../services/dtos/meeting.dto';
import StyledTable from '../../commun/table';
import NoData from '../../commun/no-data';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { FileDto } from '../../../services/dtos/file.dto';
import { HeadCellDto } from '../../../services/dtos/head-cell.dto';
import OpenFile from '../../action/open-file';
import { Form, Formik } from 'formik';
import InputForm from '../../commun/input-form';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import '../../forms/filter-form.scss';
import MeetingAttendance from '../../action/meeting-attendance';

interface MeetingFilesFilters {
    name: string;
    startDate: string;
    endDate: string;
}

const initialFilters: MeetingFilesFilters = {
    name: '',
    startDate: '',
    endDate: '',
};

const normalize = (value?: string) => (value ?? '').trim().toLowerCase();

const isSameFile = (left?: FileDto, right?: FileDto) =>
    !!left && !!right && left.data === right.data && left.meetingId === right.meetingId;

const parseMeetingTimestamp = (file: FileDto): number => {
    if (!file.meetingDate) {
        return 0;
    }

    let year = 0;
    let month = 0;
    let day = 0;

    if (file.meetingDate.includes('/')) {
        const [dayValue, monthValue, yearValue] = file.meetingDate.split('/');
        day = Number(dayValue);
        month = Number(monthValue) - 1;
        year = Number(yearValue);
    } else {
        const parsedDate = new Date(file.meetingDate);
        if (Number.isNaN(parsedDate.getTime())) {
            return 0;
        }
        year = parsedDate.getFullYear();
        month = parsedDate.getMonth();
        day = parsedDate.getDate();
    }

    const timeMatch = file.meetingStartTime?.match(/(\d{1,2})\D+(\d{2})/);
    const hours = timeMatch ? Number(timeMatch[1]) : 0;
    const minutes = timeMatch ? Number(timeMatch[2]) : 0;

    return new Date(year, month, day, hours, minutes).getTime();
};

const parseFilterDate = (value?: string): number | undefined => {
    if (!value) {
        return undefined;
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return undefined;
    }

    return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate()).getTime();
};

/**
 * Ui pour la page d'assemblée générale
 * @constructor
 */
interface MeetingFilesUiProps {
    meeting?: MeetingDto;
    files: Array<FileDto>;
    file?: FileDto;
    searching: boolean;
    handlerLoadFileContent: (file: FileDto) => Promise<FileDto | undefined>;
    handlerShowAllMeetingFiles?: () => void;
    handlerUpdateMeetingAttendance?: (meeting: MeetingDto, attendance: boolean) => Promise<MeetingDto>;
    handlerProxyChange?: (meeting: MeetingDto, myProxyGivenTo: MyProxyGivenToDto | null) => void;
}

export default function MeetingFilesUi(props: Readonly<MeetingFilesUiProps>) {
    // Document du meeting à voir et télécharger
    const [file, setFile] = useState<FileDto>();
    const [previewUrl, setPreviewUrl] = useState<string>();
    const [loadingDocument, setLoadingDocument] = useState<boolean>(false);
    const [filters, setFilters] = useState<MeetingFilesFilters>(initialFilters);
    // Création des cellules têtes du tableau
    // Les ids des cellules doivent correspondre aux noms des champs DTO, sauf l'action
    const headCells: HeadCellDto<FileDto, keyof FileDto>[] = props.meeting
        ? [{ id: 'name', label: 'Nom', sort: false, width: 90 }]
        : [
              { id: 'name', label: 'Nom', sort: false, width: 62 },
              { id: 'meetingLabel', label: 'Réunion', sort: false, width: 28 },
          ];

    const displayedFiles = (
        props.meeting
            ? [...props.files]
            : [...props.files].sort((left, right) => parseMeetingTimestamp(right) - parseMeetingTimestamp(left))
    ).filter(currentFile => {
        if (props.meeting) {
            return true;
        }

        const normalizedName = normalize(currentFile.name);
        const matchesName = !filters.name || normalizedName.includes(normalize(filters.name));

        const fileDate = parseMeetingTimestamp(currentFile);
        const startDate = parseFilterDate(filters.startDate);
        const endDate = parseFilterDate(filters.endDate);
        const matchesStartDate = startDate === undefined || fileDate >= startDate;
        const matchesEndDate = endDate === undefined || fileDate <= new Date(endDate).setHours(23, 59, 59, 999);

        return matchesName && matchesStartDate && matchesEndDate;
    });

    useEffect(() => {
        setFile(currentFile => {
            if (!currentFile) {
                return undefined;
            }

            return displayedFiles.find(candidate => isSameFile(candidate, currentFile));
        });
    }, [displayedFiles, props.files, props.meeting, filters.endDate, filters.name, filters.startDate]);

    useEffect(() => {
        if (!file?.content) {
            setPreviewUrl(undefined);
            return;
        }

        if (typeof globalThis.URL?.createObjectURL !== 'function') {
            setPreviewUrl(undefined);
            return;
        }

        const pdfBlob = new Blob([file.content], { type: 'application/pdf' });
        const objectUrl = globalThis.URL.createObjectURL(pdfBlob);
        setPreviewUrl(objectUrl);

        return () => {
            globalThis.URL.revokeObjectURL(objectUrl);
        };
    }, [file]);

    //Action à mettre dans le tableau
    const action = (paramFile: FileDto) => (
        <OpenFile handlerOpenFile={handlerOpenFile} handlerDownloadFile={handlerDownloadFile} file={paramFile} />
    );

    // Permet de télécharger un document de la réunion
    const handlerDownloadFile = async (paramFile: FileDto) => {
        setLoadingDocument(true);
        const resolvedFile = await props.handlerLoadFileContent(paramFile);
        if (!resolvedFile?.content) {
            setLoadingDocument(false);
            return;
        }
        if (typeof globalThis.URL?.createObjectURL !== 'function') {
            setLoadingDocument(false);
            return;
        }
        const fileURL = globalThis.URL.createObjectURL(resolvedFile.content);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = resolvedFile.name;
        link.click();
        globalThis.URL.revokeObjectURL(fileURL);
        setLoadingDocument(false);
    };

    // Permet de visualiser un document de la réunion
    const handlerOpenFile = async (paramFile: FileDto) => {
        setLoadingDocument(true);
        const resolvedFile = await props.handlerLoadFileContent(paramFile);
        setFile(resolvedFile ?? paramFile);
        setLoadingDocument(false);
    };

    //Retourne la vue
    return (
        <Grid container className={'tab-content'}>
            <Grid item xs={12}>
                <Typography variant="h3" className={'title'}>
                    <InsertDriveFileOutlinedIcon /> Documents administratifs
                </Typography>
            </Grid>
            <Grid item xs={12}>
                <Typography variant="h5" className={'title'}>
                    {props.meeting
                        ? `Réunion du ${props.meeting.date} à ${props.meeting.startTime}`
                        : 'Ensemble des documents des assemblées générales'}
                </Typography>
                {props.meeting?.location && <Typography variant="body1">Lieu : {props.meeting.location}</Typography>}
                {props.meeting?.pointsReunion && (
                    <Box>
                        <Typography variant="body1">Points de réunion :</Typography>
                        <Typography component="div" dangerouslySetInnerHTML={{ __html: props.meeting.pointsReunion }} />
                    </Box>
                )}
            </Grid>
            {props.meeting && props.handlerUpdateMeetingAttendance && (
                <Grid item xs={12} sx={{ marginBottom: '12px' }}>
                    <MeetingAttendance
                        meeting={props.meeting}
                        onChange={attendance => props.handlerUpdateMeetingAttendance?.(props.meeting!, attendance)}
                        onProxyChange={myProxyGivenTo => props.handlerProxyChange?.(props.meeting!, myProxyGivenTo)}
                    />
                </Grid>
            )}
            {props.meeting && props.handlerShowAllMeetingFiles && (
                <Grid item xs={12}>
                    <Button
                        variant="outlined"
                        className="filter-form__button filter-form__button--reset"
                        onClick={props.handlerShowAllMeetingFiles}
                        sx={{ marginBottom: '12px' }}
                    >
                        Voir l'ensemble des documents
                    </Button>
                </Grid>
            )}
            {!props.meeting && (
                <Grid item xs={12}>
                    <Formik initialValues={initialFilters} onReset={setFilters} onSubmit={setFilters}>
                        <Form>
                            <Paper className="filter-form" elevation={0}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} md={5}>
                                        <InputForm id="name" name="name" label="Nom du document" />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <InputForm id="startDate" name="startDate" label="Après la date" type="date" />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <InputForm id="endDate" name="endDate" label="Avant la date" type="date" />
                                    </Grid>
                                    <Grid item xs={12} md={3}>
                                        <div className="filter-form__actions">
                                            <Button
                                                variant="outlined"
                                                type="reset"
                                                className="filter-form__button filter-form__button--reset"
                                                startIcon={<ClearIcon />}
                                            >
                                                Annuler
                                            </Button>
                                            <Button
                                                type="submit"
                                                variant="contained"
                                                className="filter-form__button"
                                                startIcon={<SearchIcon />}
                                            >
                                                Rechercher
                                            </Button>
                                        </div>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Form>
                    </Formik>
                </Grid>
            )}
            {/* On affiche d'abord les réunions à venir */}
            <Grid item xs={12} md={6} sx={{ marginBottom: '20px' }}>
                {displayedFiles.length === 0 ? (
                    <NoData
                        title={'Information : '}
                        content={
                            props.meeting
                                ? 'Aucune information à venir'
                                : 'Aucun document administratif disponible pour le moment'
                        }
                    />
                ) : (
                    <StyledTable
                        data={displayedFiles}
                        searching={props.searching}
                        action={action}
                        headCells={headCells}
                        minWidth="100%"
                        // handleOnPageChange={() => {}}
                    ></StyledTable>
                )}
            </Grid>
            {/* On affiche le document */}
            <Grid item xs={12} md={6} sx={{ marginBottom: '20px', display: 'flex', justifyContent: 'center' }}>
                <Paper
                    elevation={0}
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        height: { xs: '420px', md: '72vh' },
                        justifyContent: 'center',
                        overflow: 'hidden',
                        padding: 1,
                        width: '100%',
                    }}
                >
                    {previewUrl !== undefined && (
                        <iframe
                            data-testid={'pdf'}
                            title={`Aperçu du document ${file?.name ?? ''}`}
                            style={{ height: '100%', width: '100%' }}
                            src={previewUrl}
                        />
                    )}
                    {previewUrl === undefined && loadingDocument && (
                        <Typography variant="body2" sx={{ color: '#55718f' }}>
                            Chargement du document...
                        </Typography>
                    )}
                    {previewUrl === undefined && !loadingDocument && (
                        <Typography variant="body2" sx={{ color: '#55718f' }}>
                            Sélectionnez un document pour l&apos;afficher.
                        </Typography>
                    )}
                </Paper>
            </Grid>
        </Grid>
    );
}
